import React from 'react';
import { graphql, PageProps } from 'gatsby';
import Layout from 'src/elements/layout/layout';
import { formatElementFromHtmlAst } from 'src/functions/formatElement.htmlAst';
import { TemplateContainer, TemplateEncyclopediaPageTitle, TemplateContent, TemplateTitle, TemplateTitleGoBackButton } from './encyclopedia.styles';
import Heading from 'src/elements/typography/heading';
import Paragraph from 'src/elements/typography/paragraph';
import FadeInAnimationWrapper from 'src/elements/animation/fade-in/FadeInAnimationWrapper';
import calculateEstimatedReadingTime from 'src/functions/calculateEstimatedReadingTime';

interface MarkupValue {
  type: 'text';
  value: string;
}

interface MarkupElement {
  type: 'element';
  tagName: 'a' | 'p' | 'h1' | 'h2' | 'h3' | 'h4' | 'h5' | 'strong' | 'ul' | 'ol' | 'li' | 'em' | 'img';
  properties: {
    href?: string;
    src?: string;
  }
  children: (MarkupElement | MarkupValue)[]
}


interface EncyclopediaProps extends PageProps {
  data: {
    contentfulEncyclopedia: {
      id: string;
      title: string;
      slug: string;
      seoTitle?: string;
      seoDescription?: string;
      contentful_id: string;
      body: {
        internal: { type: string }
        childMarkdownRemark: {
          wordCount?: {
            words?: number
          }
          htmlAst: {
            type: string;
            children: MarkupElement[]
          }
        }
      }
    }
  }
}

const EncyclopediaTemplate = (props: EncyclopediaProps): JSX.Element => {
  const encyclopediaData = props.data.contentfulEncyclopedia;

  return (
    <Layout
      helmet={{
        title: encyclopediaData.seoTitle || `${encyclopediaData.title} | Mighty Networks`,
        description: encyclopediaData.seoDescription,
        estimatedReadingTime: calculateEstimatedReadingTime(encyclopediaData.body.childMarkdownRemark?.wordCount?.words)
      }}
      location={props.location}
    >
      <FadeInAnimationWrapper>
        <TemplateContainer>
          <TemplateEncyclopediaPageTitle>
            <Heading
              text="The Mighty Encyclopedia"
              size="3.4rem"
              sizeLg="2.5em"
              sizeSm="1.5em"
              align="center"
            />
            <Paragraph
              text="Learn everything there is to know about running a"
              size="1.5rem"
              sizeLg="1.3rem"
              sizeSm="0.9rem"
              weight="light"
              align="center"
            />
            <Paragraph
              text="successful Mighty Network"
              size="1.5rem"
              sizeLg="1.3rem"
              sizeSm="0.9rem"
              weight="light"
              align="center"
            />
          </TemplateEncyclopediaPageTitle>
          <TemplateTitle>
            <TemplateTitleGoBackButton to={'/encyclopedia'}>
              <svg xmlns="http://www.w3.org/2000/svg" enableBackground="new 0 0 512 512" version="1.1" viewBox="0 0 512 512">
                <path d="m150.11 242.03 174.54-232.72c7.714-10.283 22.298-12.365 32.582-4.655 10.283 7.713 12.367 22.3 4.655 32.582l-164.07 218.76 164.07 218.77c7.713 10.282 5.627 24.871-4.655 32.582-4.186 3.14-9.086 4.656-13.945 4.656-7.076 0-14.064-3.215-18.637-9.311l-174.54-232.73c-6.205-8.274-6.205-19.653 1e-3 -27.927z" fill="#191F28" />
              </svg>
            </TemplateTitleGoBackButton>
            <Heading
              text={encyclopediaData.title}
              size="2.5rem"
              sizeLg="2rem"
              sizeSm="1.5rem"
            />
          </TemplateTitle>
          <TemplateContent>
            {encyclopediaData.body.childMarkdownRemark.htmlAst.children.map((child, index) => formatElementFromHtmlAst(child, `${index}`))}
          </TemplateContent>
        </TemplateContainer>
      </FadeInAnimationWrapper>
    </Layout>
  )
}

export default EncyclopediaTemplate

export const pageQuery = graphql`
  query EncyclopediaBySlug($slug: String!) {
    contentfulEncyclopedia(slug: { eq: $slug }) {
      id
      title
      slug
      seoTitle
      seoDescription
      contentful_id
      body  {
        internal {
          type
        }
        childMarkdownRemark {
          htmlAst
          wordCount {
            words
          }
        }
      }
    }
  }
`
